
import { IDetailsRowProps, SelectionMode, IColumn } from 'office-ui-fabric-react/lib/DetailsList';
import * as React from 'react';
import { WithItemCache, IItemCacheStateProps } from '../withItemCache/WithItemCache';
import { getRowAriaLabel as defaultGetRowAriaLabel } from '../../models/items/AriaHelper';
import { getRowCheckboxAriaLabel } from '../../models/items/AriaHelper';
import { ItemDefaultDropZone } from '../defaultDropZone/DefaultDropZone';
import { mark } from '../../utilities/performance';
import { IActionStateProps, WithAction, evaluateAction } from '../withAction/WithAction';
import { itemAriaLabelControl } from '../../controls/itemAriaLabelControl';
import { IItemColumn } from '../../models/schema/IItemColumn';
import { IItemColumnRendererProps } from '../../models/schema/itemColumnRenderControl';
import { IItemCache } from '../../models/IItemCacheObserver';
import { folderFacet } from '../../facets/ItemFacets';
import { TelemetryProvider } from '../telemetry/TelemetryProvider';
import { IEventContext } from '@ms/telemetry-manager/lib/IEvent';
import { IPropsWithDefaultRender } from '../controls/defaultRenderComponentControlOption';
import { ItemDefaultClickAction } from '../actions/defaultClick/DefaultClickAction';
import { RegisterInvokeAction } from '../decorators/withActionInvoker';
import { BackgroundTaskContextProvider } from '../backgroundTaskRunner/BackgroundTaskHost';

const DETAILS_ROW_TELEMETRY_CONTEXT: IEventContext = {
  component: 'DetailsRow'
};

/**
 * Props used to render an item as a Fabric DetailsRow.
 * @public
 */
export interface IItemDetailsRowProps extends IDetailsRowProps {
  item: IItemColumnRendererProps;

  /**
   * The key for the item to render in a row.
   */
  itemKey: string;
  /**
   * The key for the item set for the overall list.
   */
  itemSetKey: string;
}

interface IColumnRenderMap {
  renderers: {
    [key: string]: IColumn['onRender'];
  };
  columns: IColumn[];
}

/**
 * Component which renders a Details Row for a single item.
 * @internal
 */
export class ItemDetailsRow extends React.PureComponent<IPropsWithDefaultRender<IItemDetailsRowProps>> {
  public render(): JSX.Element {
    const {
      defaultRender: DetailsRow,
      ...detailsRowProps
    } = this.props;

    const {
      itemKey,
      columns,
    } = this.props;

    return (
      <TelemetryProvider
        withContext={ DETAILS_ROW_TELEMETRY_CONTEXT }
      >
        <BackgroundTaskContextProvider scope='local'>
          <WithItemCache>
            { ({ itemCache }: IItemCacheStateProps) => {
              const ariaLabelControlHandler = itemCache.demandItemFacet(itemAriaLabelControl, itemKey);

              // This is the place to bring in any props initialized from the state of the item cache.
              // Use this point to override DetailsRow props based on the state of the item.

              const {
                itemAriaLabel,
                itemCheckboxAriaLabel
              } = itemAriaLabelControl.evaluate(ariaLabelControlHandler)(itemCache, {
                itemKey: itemKey,
                itemAriaLabel: defaultGetRowAriaLabel(itemCache, { itemKey: itemKey }),
                itemCheckboxAriaLabel: getRowCheckboxAriaLabel(itemCache, { itemKey: itemKey })
              });

              const getRowAriaLabel = () => itemAriaLabel;
              mark(`ItemDetailsRow.render(${itemKey})`);

              const {
                renderers,
                columns: finalColumns
              } = buildColumnRenderMap(itemCache, {
                itemKey,
                columns
              });

              const isFolder = itemCache.demandItemFacet(folderFacet, itemKey);

              const invokeAction = evaluateAction(itemCache, (
                <ItemDefaultClickAction itemKey={ itemKey } />
              ));

              return (
                // Render the action used to open the item, so that it bubbles up its availability and execute function
                // to the action dispatcher.
                <WithAction action={ invokeAction }>
                  { (invokeActionStateProps: IActionStateProps) => {
                    const { isAvailable } = invokeActionStateProps;

                    const isDisabled = !isAvailable && (
                      !detailsRowProps.selection.canSelectItem(detailsRowProps.item) ||
                      detailsRowProps.selection.mode === SelectionMode.none);

                    const item: IItemColumnRendererProps & Pick<IColumnRenderMap, 'renderers'> = {
                      ...detailsRowProps.item,
                      isInvokable: isAvailable,
                      isDisabled: isDisabled,
                      renderers
                    };

                    const row = (
                      <>
                        <RegisterInvokeAction
                          { ...invokeActionStateProps }
                          itemKey={ itemKey }
                          index={ detailsRowProps.itemIndex }
                        />
                        {
                          DetailsRow ? (
                            <DetailsRow
                              { ...detailsRowProps }
                              columns={ finalColumns }
                              item={ item }
                              onRenderItemColumn={ this._onRenderItemColumn }
                              getRowAriaLabel={ getRowAriaLabel }
                              checkButtonAriaLabel={ itemCheckboxAriaLabel }
                            />
                          ) : null
                        }
                      </>
                    );

                    return isFolder && row ? (
                      <ItemDefaultDropZone itemKey={ itemKey }>
                        { row }
                      </ItemDefaultDropZone>
                    ) : row;
                  } }
                </WithAction>
              );
            } }
          </WithItemCache>
        </BackgroundTaskContextProvider>
      </TelemetryProvider>
    );
  }

  private _onRenderItemColumn = (
    item: IItemColumnRendererProps & Pick<IColumnRenderMap, 'renderers'>,
    index?: number,
    column?: IColumn
  ): JSX.Element | null => {
    if (!column || !column.key) {
      return null;
    }

    const onRender = item.renderers[column.key] || column.onRender;

    if (onRender) {
      return onRender(item, index, column);
    }

    return null;
  }
}

function buildColumnRenderMap(itemCache: IItemCache, props: {
  itemKey: string;
  columns: IItemColumn[];
}): IColumnRenderMap {
  const {
    itemKey,
    columns
  } = props;
  const renderers: IColumnRenderMap['renderers'] = {};
  const finalColumns: IColumn[] = [];

  for (const column of columns) {
    const {
      data
    } = column;

    let {
      onRender,
      ...trimmedColumn
    } = column;

    if (data && data.renderHandler) {
      // Techically, this is a control handler.
      // However, given the performance criticality, it's expedient to evaluate it
      // directly rather than via wrappers.
      ({
        renderer: onRender = onRender
      } = data.renderHandler(itemCache, {
        itemKey,
        renderer: onRender
      }));
    }

    // TODO Pre-render the fields using the Item Cache, if the output of the renderer is compatible.

    renderers[column.key] = onRender;

    // Push a 'trimmed' version of the column so its default render behavior does not apply.
    finalColumns.push(trimmedColumn);
  }

  return {
    renderers,
    columns: finalColumns
  };
}
